/**
 * @contract
 *   Назначение:  Форма редактирования SEO-описания, контактов и логотипа ЧОП
 *                для кабинета владельца профиля
 *   Компонент:  SeoDescriptionEditor (client-side)
 *   KEYWORDS:   SEO, Profile, Description, Contacts, Logo
 *   LINKS:      USES_API(seo_companies.seo_description)
 *               USES_API(seo_companies.contact_phone)
 *               USES_API(seo_companies.contact_email)
 */

"use client";

import React, { useState } from "react";
import { Save, Loader2, FileText, Phone, Mail, Image } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { Input } from "@oxpa/shared/ui/input";
import { Textarea } from "@oxpa/shared/ui/textarea";
import { Label } from "@oxpa/shared/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@oxpa/shared/ui/card";
import { useToast } from "@oxpa/shared/hooks/use-toast";
import { cn } from "@oxpa/shared/lib/utils";

// # START_INTERFACE_SeoProfileData
export interface SeoProfileData {
  /** SEO-описание компании (макс 2000 символов) */
  seo_description: string;
  /** Контактный телефон */
  contact_phone: string;
  /** Контактный email */
  contact_email: string;
}
// # END_INTERFACE_SeoProfileData

// # START_INTERFACE_SeoDescriptionEditorProps
interface SeoDescriptionEditorProps {
  /** Текущее SEO-описание */
  initialDescription: string | null;
  /** Текущие контакты */
  initialContacts: {
    phone?: string;
    email?: string;
  };
  /** Функция сохранения */
  onSave: (data: SeoProfileData) => Promise<{ success: boolean; error?: string }>;
}
// # END_INTERFACE_SeoDescriptionEditorProps

const MAX_DESCRIPTION_LENGTH = 2000;

// # START_FUNCTION_SeoDescriptionEditor
export function SeoDescriptionEditor({
  initialDescription,
  initialContacts,
  onSave,
}: SeoDescriptionEditorProps) {
  const { toast } = useToast();
  const [description, setDescription] = useState(initialDescription ?? "");
  const [contactPhone, setContactPhone] = useState(initialContacts.phone ?? "");
  const [contactEmail, setContactEmail] = useState(initialContacts.email ?? "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (description.length > MAX_DESCRIPTION_LENGTH) {
      toast({
        variant: "destructive",
        title: "Ошибка",
        description: `Описание превышает лимит в ${MAX_DESCRIPTION_LENGTH} символов.`,
      });
      return;
    }

    setSaving(true);
    try {
      const result = await onSave({
        seo_description: description.trim(),
        contact_phone: contactPhone.trim(),
        contact_email: contactEmail.trim(),
      });

      if (result.success) {
        toast({
          title: "Сохранено",
          description: "Данные публичного профиля обновлены.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Ошибка",
          description: result.error ?? "Не удалось сохранить данные.",
        });
      }
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Ошибка",
        description: e?.message ?? "Произошла непредвиденная ошибка.",
      });
    } finally {
      setSaving(false);
    }
  };

  const charsLeft = MAX_DESCRIPTION_LENGTH - description.length;
  const isOverLimit = charsLeft < 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="w-4 h-4 text-muted-foreground" />
          Публичный профиль
        </CardTitle>
        <CardDescription>
          Информация отображается на публичной странице вашего ЧОПа.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* SEO-описание */}
        <div className="space-y-2">
          <Label htmlFor="seo-description" className="text-sm font-medium">
            SEO-описание компании
          </Label>
          <Textarea
            id="seo-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Расскажите о вашем охранном предприятии: направления деятельности, зона покрытия, опыт работы..."
            maxLength={MAX_DESCRIPTION_LENGTH + 100}
            rows={5}
            className="resize-y min-h-[100px]"
          />
          <div className="flex justify-between items-center">
            <p className="text-[11px] text-muted-foreground">
              Будет отображаться в поисковой выдаче и на странице компании.
            </p>
            <span
              className={cn(
                "text-xs tabular-nums",
                isOverLimit ? "text-destructive font-semibold" : "text-muted-foreground"
              )}
            >
              {description.length}/{MAX_DESCRIPTION_LENGTH}
            </span>
          </div>
        </div>

        {/* Контактный телефон */}
        <div className="space-y-2">
          <Label htmlFor="contact-phone" className="text-sm font-medium">
            <Phone className="w-3.5 h-3.5 inline mr-1.5 text-muted-foreground" />
            Контактный телефон
          </Label>
          <Input
            id="contact-phone"
            type="tel"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="+7 (XXX) XXX-XX-XX"
            maxLength={20}
          />
        </div>

        {/* Контактный email */}
        <div className="space-y-2">
          <Label htmlFor="contact-email" className="text-sm font-medium">
            <Mail className="w-3.5 h-3.5 inline mr-1.5 text-muted-foreground" />
            Контактный email
          </Label>
          <Input
            id="contact-email"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="chop@example.ru"
            maxLength={100}
          />
        </div>

        {/* Кнопка сохранения */}
        <div className="flex justify-end pt-2 border-t">
          <Button
            onClick={handleSave}
            disabled={saving || isOverLimit}
            className="gap-2"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saving ? "Сохранение..." : "Сохранить"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
// # END_FUNCTION_SeoDescriptionEditor


